import {Injectable} from '@angular/core';
import {DataService} from './data.service';
import {firstValueFrom} from 'rxjs';
import {PrintBuilderService} from './print-builder.service';
import {BsToastService} from './bs-toast-service';

@Injectable({providedIn: 'root'})
export class ShipmentDocumentService {
  constructor(
    private data: DataService,
    private printer: PrintBuilderService,
    private toast: BsToastService,
  ) {
  }

  /**
   * Print a shipment document by its header id, reusing the same HTML as shipment-form.print().
   * Optionally pass subLedger ("ar" or "ap"). If omitted, we try to infer it from the loaded entry.
   */
  async printById(id: string, subLedger?: string): Promise<void> {
    try {
      const vm = await this.buildVmForPrint(id, subLedger);
      const html = this.printer.buildPrintHtml(vm, 'shipment');
      this.printHtmlInIframe(html);
    } catch (e) {
      console.error('Shipment print failed', e);
      this.toast.showErrorToast($localize`:Unable to print document@@unableToPrint:Unable to print document`);
    }
  }

  /**
   * Load and normalize all data needed for printing, mirroring shipment-form.print() mappings.
   */
  private async buildVmForPrint(id: string, subLedger?: string): Promise<any> {
    // Load shipment and reference data in parallel
    // Match endpoints and patterns used by shipment-form to avoid 404s and keep data shapes consistent
    const [entry, uom, entities, companies, shipTo, billTo, inventory] = await Promise.all([
      firstValueFrom(this.data.getData('entry', id)),
      firstValueFrom(this.data.getData('uom', '?state=active&pageNumber=0&pageSize=999999')).then((r: any) => r?.Data ?? r),
      // shipment-form uses getData('entity', '?...'), but we accept either shape; normalize to .Data if present
      firstValueFrom(this.data.getData('entity', '?state=active&pageNumber=0&pageSize=999999')).then((r: any) => r?.Data ?? r),
      firstValueFrom(this.data.getData('company', '?state=active&pageNumber=0&pageSize=999999')).then((r: any) => r?.Data ?? r),
      firstValueFrom(this.data.getData('entity_address/addresses', '?type=ship')).then((r: any) => r?.Data ?? r).catch(() => []),
      firstValueFrom(this.data.getData('entity_address/addresses', '?type=bill')).then((r: any) => r?.Data ?? r).catch(() => []),
      // shipment-form loads inventory items from the 'item' endpoint, not 'inventory'
      firstValueFrom(this.data.getData('item', '?state=active&pageNumber=0&pageSize=999999')).then((r: any) => r?.Data ?? r).catch(() => []),
    ]);

    const vm: any = {
      item: entry || {},
      printItem: {},
      uom,
      entity: entities,
      company: companies,
      shipToAddress: shipTo,
      billToAddress: billTo,
      inventoryItem: inventory,
      subLedger: this.resolveSubLedger(entry, subLedger),
    };

    // Map UOM codes
    const uomById: Record<string, any> = {};
    (Array.isArray(uom) ? uom : []).forEach((u: any) => {
      if (u && u.id != null) {
        uomById[String(u.id)] = u;
      }
    });
    (Array.isArray(vm.item?.detail) ? vm.item.detail : []).forEach((d: any) => {
      if (d?.uom_id != null && uomById[String(d.uom_id)]) {
        d.uom = uomById[String(d.uom_id)].code;
      }
    });

    // entityInput (display name)
    try {
      const e = (Array.isArray(entities) ? entities : []).find((x: any) => x?.id == vm.item?.header?.entity_id);
      if (e) {
        if (!vm.item.header) {
          vm.item.header = {} as any;
        }
        vm.item.header.entityInput = e.name;
      }
    } catch {
    }

    // bill/ship address into printItem
    const header = vm.item?.header || {};
    try {
      const bill = (Array.isArray(billTo) ? billTo : []).find((x: any) => x?.id == header?.bill_address_id);
      if (bill) {
        vm.printItem.billToAddress1 = bill.address_1;
        vm.printItem.billToAddress2 = `${bill.city || ''}${bill.state ? ', ' + bill.state : ''}  ${bill.zip || ''}`.trim();
      }
    } catch {
    }
    try {
      const ship = (Array.isArray(shipTo) ? shipTo : []).find((x: any) => x?.id == header?.ship_address_id);
      if (ship) {
        vm.printItem.shipToAddress1 = ship.address_1;
        vm.printItem.shipToAddress2 = `${ship.city || ''}${ship.state ? ', ' + ship.state : ''}  ${ship.zip || ''}`.trim();
      }
    } catch {
    }

    // company block
    try {
      const comp = (Array.isArray(companies) ? companies : []).find((x: any) => x?.id == header?.company_id) || {};
      vm.printItem.companyLogo = comp.logo;
      vm.printItem.companyName = comp.name;
      vm.printItem.companyAddress1 = comp.address_1;
      vm.printItem.companyAddress2 = `${comp.city || ''} ${comp.state || ''} ${comp.zip || ''}`.trim();
      vm.printItem.companyTaxNumber = comp.tax_number;
      vm.printItem.companyPhone = comp.phone;
    } catch {
    }

    // Map itemInput codes from inventory
    const itemById: Record<string, any> = {};
    (Array.isArray(inventory) ? inventory : []).forEach((it: any) => {
      if (it && it.id != null) {
        itemById[String(it.id)] = it;
      }
    });
    (Array.isArray(vm.item?.detail) ? vm.item.detail : []).forEach((d: any) => {
      const it = d?.item_id != null ? itemById[String(d.item_id)] : null;
      if (it) {
        d.itemInput = it.code;
      }
    });

    // Try to resolve orderNumber like shipment-form does
    try {
      vm.orderNumber = vm.orderNumber
        || vm?.order?.header?.number
        || vm?.item?.order?.header?.number
        || header?.linked_header_number
        || header?.order_number
        || '';
    } catch {
    }

    // If still missing, and we have a linked header id, fetch it like the form does
    try {
      if ((!vm.orderNumber || String(vm.orderNumber).trim() === '') && header?.linked_header_id) {
        const linked = await firstValueFrom(this.data.getData('entry', header.linked_header_id));
        const linkedNum = linked?.header?.number || linked?.item?.header?.number;
        if (linkedNum) {
          vm.orderNumber = linkedNum;
        }
      }
    } catch {
      // best-effort only; ignore failures and continue
    }

    return vm;
  }

  private resolveSubLedger(entry: any, override?: string): string {
    if (override) {
      return override.toLowerCase();
    }
    try {
      const bt = entry?.batch?.type_id || entry?.batchTypeId || entry?.item?.batchTypeId;
      if (String(bt) === '12') {
        return 'ap';
      }
      if (String(bt) === '11') {
        return 'ar';
      }
    } catch {
    }
    return 'ar';
  }

  private printHtmlInIframe(html: string) {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    const doPrint = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } catch {
      } finally {
        setTimeout(() => {
          try {
            iframe.remove();
          } catch {
          }
        }, 400);
      }
    };
    iframe.onload = doPrint;
    (iframe as any).srcdoc = html;
    document.body.appendChild(iframe);
  }
}
